<script>
import { mapState } from "pinia";
import { useAppStore } from "@/store/app.store";
import { useChatStore } from "@/store/chat.store";
import { defineComponent } from "vue";
import { ChatService } from "@/services/socket/chat.service.ts";
import { useCache } from "@/composables/useCache.js";
import dayjs from "dayjs";

const {
  getCachedThreads,
  upsertCachedThread,
  setThreadsTimeFrom,
  getThreadsTimeFrom,
  getUseCache,
  clearCachedThreads,
} = useCache();

export default defineComponent({
  name: "ChatMixin",
  computed: {
    ...mapState(useAppStore, ["dispatcher"]),
    ...mapState(useChatStore, ["threads", "chatService"]),
  },
  methods: {
    async handleChatSocket(connection) {
      const chatStore = useChatStore();

      const chatService = new ChatService(connection);
      chatStore.setChatService(chatService);

      this.chatService.subscribe();
      await this.loadAllThreads();
    },
    disconnectChat() {
      if (this.chatService) {
        this.chatService.unsubscribe();
        this.chatService = null;
      }
    },
    showMessageTime(dt) {
      const d = this.$dayjs.utc(dt).tz(this.dispatcher.timezone);
      const isToday = d.isSame(new Date(), "day");
      return d.format(isToday ? "HH:mm z" : "MM/DD/YY HH:mm z");
    },
    async noResponseThread() {
      try {
        const resp = await this.$api.chat.noResponseThread(this.thread.id);
        if (resp.success) {
          this.viewThread(this.thread);
        }
      } catch (err) {
        console.error("Can not set no response thread", err);
      }
    },
    async resolveThread() {
      await this.updateThread({ dispatcherId: this.dispatcher.id, resolved: true });
    },
    viewThread(thread, limit = 25, timeFrom = null) {
      if (!thread || !thread.id || !this.chatService) {
        return;
      }

      const chatStore = useChatStore();

      chatStore.setViewedThreadId(thread.id);
      this.chatService.sendMessagesUpdate(thread.id, limit, timeFrom);
    },
    async updateThread(data) {
      try {
        const resp = await this.$api.chat.updateThread(this.thread.id, data);
        if (resp.success) {
          this.viewThread(this.thread);
        }
      } catch (err) {
        console.error("Can not update thread", err);
      }
    },
    async loadAllThreads() {
      let offset = 0;
      const useCache = getUseCache();
      const chatStore = useChatStore();
      const timeFrom = getThreadsTimeFrom();
      const timeFromUnix = timeFrom && useCache ? dayjs(timeFrom).unix() : null;

      for (;;) {
        try {
          const newTimeFrom = dayjs().toISOString();

          const resp = await this.$api.chat.getThreads(offset, 300, timeFromUnix);

          if (!resp.success) {
            return;
          }

          if (resp.data.threads.length === 0) {
            if (useCache) {
              await getCachedThreads();
              setThreadsTimeFrom(newTimeFrom);
            } else {
              const cachedThreads = await getCachedThreads(false);

              if (cachedThreads.length) {
                await clearCachedThreads();
              }
            }

            break;
          }

          if (useCache) {
            await Promise.all(
              resp.data.threads.map(async (thread) => {
                await upsertCachedThread(thread, false);
              }),
            );
          } else {
            chatStore.upsertThreads(resp.data.threads);
          }

          offset += resp.data.threads.length;
        } catch (e) {
          console.error("Can t update threads list", e);
          break;
        }
      }

      chatStore.sortThreads();
      chatStore.recalcThreadsCount();
    },
  },
});
</script>
